import { instance } from "api/apiBase";

interface MhvillageStatusResponse {
  connected: boolean;
  parsing_email?: string;
}

export interface MhvillageExternalMapping {
  id: number;
  external_id: string;
  property_id: number | null;
  property_name: string | null;
  property_address: string | null;
  is_active: boolean;
}

export interface CreateExternalMappingPayload {
  external_id: string;
  property_id?: number | null;
}

export interface UpdateExternalMappingPayload {
  external_id?: string;
  property_id?: number | null;
}

const BASE_ENDPOINT_URL = "/portal/v1/external_integrations/mhvillage";
const EXTERNAL_MAPPINGS_URL = `${BASE_ENDPOINT_URL}/external_mappings`;

export const mhvillageApi = {
  getStatus: async (): Promise<MhvillageStatusResponse> => {
    const { data } =
      await instance.get<MhvillageStatusResponse>(BASE_ENDPOINT_URL);
    return data;
  },

  connect: async (): Promise<MhvillageStatusResponse> => {
    const { data } =
      await instance.post<MhvillageStatusResponse>(BASE_ENDPOINT_URL);
    return data;
  },

  disconnect: async (): Promise<MhvillageStatusResponse> => {
    const { data } =
      await instance.delete<MhvillageStatusResponse>(BASE_ENDPOINT_URL);
    return data;
  },

  listExternalMappings: async (): Promise<MhvillageExternalMapping[]> => {
    const { data } = await instance.get<MhvillageExternalMapping[]>(
      EXTERNAL_MAPPINGS_URL
    );
    return data;
  },

  createExternalMapping: async (
    payload: CreateExternalMappingPayload
  ): Promise<MhvillageExternalMapping> => {
    const { data } = await instance.post<MhvillageExternalMapping>(
      EXTERNAL_MAPPINGS_URL,
      payload
    );
    return data;
  },

  updateExternalMapping: async (
    id: number,
    payload: UpdateExternalMappingPayload
  ): Promise<MhvillageExternalMapping> => {
    const { data } = await instance.patch<MhvillageExternalMapping>(
      `${EXTERNAL_MAPPINGS_URL}/${id}`,
      payload
    );
    return data;
  },

  deleteExternalMapping: async (id: number): Promise<void> => {
    await instance.delete(`${EXTERNAL_MAPPINGS_URL}/${id}`);
  },

  // CSV bulk import. Overwrites the entire mapping set for the partner:
  // the backend parses only the first column of each line as `external_id`,
  // wipes existing rows, and returns the new full set (property_id null).
  importExternalMappings: async (
    file: File
  ): Promise<MhvillageExternalMapping[]> => {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await instance.post<MhvillageExternalMapping[]>(
      `${EXTERNAL_MAPPINGS_URL}/import`,
      formData
    );
    return data;
  },

  // Parses the file server-side with the same rules as the real import, but writes nothing.
  previewImportExternalMappings: async (
    file: File
  ): Promise<{ external_ids: string[] }> => {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await instance.post<{ external_ids: string[] }>(
      `${EXTERNAL_MAPPINGS_URL}/import/preview`,
      formData
    );
    return data;
  }
};
